//
// CardHeader
//

import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { useDrag, useDrop } from 'react-dnd';

import { emptyArray } from '../../utils';

import Row from './Row';

// Each tab is its own rounded top sitting on the framed block that follows the row,
// with the page showing between them. The row overlaps that block's top border by
// one pixel (see the Row below), so the selected tab, white and open at the bottom,
// merges into the block while the others close themselves with their own border
const tabClassName = (selected, compact, invalid, aboveCaseMessages) => classNames(
  'flex items-center justify-center shrink-0 whitespace-nowrap border border-solid',
  {
    'rounded-t-xl': !compact,
    'rounded-t-md': compact,
    'border-pxMainOutline': (!invalid) && (!aboveCaseMessages),
    'border-pxCaseHeaderSecondaryBrdr': (!invalid) && aboveCaseMessages,
    'border-pxUiAlert': invalid,
    'gap-2 text-base px-3 py-1': !compact,
    'gap-1 text-sm px-2 py-0.5': compact,
    'bg-white border-b-0 font-normal': selected && (!compact),
    'bg-white border-b-0 font-semibold': selected && compact,
    'bg-white/50 text-pxTxtLink hover:bg-white/70': !selected,
  },
);

// Width of the fade drawn on a strip side where tabs continue out of view
const fadePx = 24;

const tabContent = (choice) => (
  <>
    {(choice.imgSrc)
      ? (
          <div className="h-5 w-5 shrink-0 overflow-hidden">
            <img src={choice.imgSrc} alt={choice.txt} className="w-full h-full object-contain" />
          </div>
        )
      : null}
    {choice.txt}
  </>
);

// Only mounted when the header asks for draggable tabs: useDrag needs a drag and
// drop provider above it, which not every page carrying a CardHeader has
function CardHeaderDragTab(props) {
  const {
    choice,
    selected,
    onClick,
    disabled,
    dragType,
    moveChoice,
    compact,
    aboveCaseMessages,
  } = props;

  const [dragCollected, dragRef] = useDrag(
    () => ({
      type: dragType,
      item: { code: choice.code },
      collect: (monitor) => ({
        isDragging: !!monitor.isDragging(),
      }),
    }),
    [dragType, choice.code],
  );

  // A tab dropped onto another one moves before it
  const [dropCollected, dropRef] = useDrop(
    () => ({
      accept: dragType,
      drop: (droppedItem) => {
        if (moveChoice && (droppedItem.code !== choice.code)) {
          moveChoice(droppedItem.code, choice.code);
        }
      },
      collect: (monitor) => ({
        isOver: !!monitor.isOver() && (monitor.getItem()?.code !== choice.code),
      }),
    }),
    [dragType, choice.code, moveChoice],
  );

  const { isDragging } = dragCollected;
  const { isOver } = dropCollected;

  return (
    <button
      ref={(element) => {
        dragRef(element);
        dropRef(element);
      }}
      type="button"
      id={choice.code}
      onClick={onClick}
      className={classNames(
        tabClassName(selected, compact, choice.invalid, aboveCaseMessages),
        {
          'cursor-grab': !isDragging,
          'cursor-grabbing opacity-60': isDragging,
          'border-l-2 border-l-pxPrimary': isOver && moveChoice,
        },
      )}
      disabled={disabled}
    >
      {tabContent(choice)}
    </button>
  );
}

CardHeaderDragTab.propTypes = {
  choice: PropTypes.shape({
    code: PropTypes.string.isRequired,
    txt: PropTypes.string.isRequired,
    imgSrc: PropTypes.string,
    invalid: PropTypes.bool,
  }).isRequired,
  selected: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  dragType: PropTypes.string.isRequired,
  moveChoice: PropTypes.func,
  compact: PropTypes.bool,
  aboveCaseMessages: PropTypes.bool,
};

const CardHeader = function CardHeader(props) {
  const {
    choices = emptyArray,
    selected,
    select,
    title,
    titleId,
    disabled,
    dragType,
    moveChoice,
    leading,
    trailing,
    compact,
    aboveCaseMessages,
  } = props;

  const onClick = (event) => {
    if (select) select(event?.currentTarget?.id);
  };

  // Tabs scroll sideways between the title and the actions. The selected one is
  // centred whenever it changes or the list grows, so its neighbours on both sides
  // stay in view and reachable
  const tabsRef = useRef(null);
  useEffect(
    () => {
      const strip = tabsRef.current;
      if (!strip) return;
      const tab = Array.from(strip.children).find((element) => (element.id === selected));
      if (!tab) return;

      // offsetLeft counts from the strip, positioned for that purpose
      strip.scrollTo({
        left: tab.offsetLeft - ((strip.clientWidth - tab.offsetWidth) / 2),
        behavior: 'smooth',
      });
    },
    [selected, choices.length],
  );

  // A vertical wheel over the strip scrolls it sideways; the page keeps the wheel
  // while the tabs fit. Attached by hand because React registers wheel as passive,
  // which forbids preventDefault
  useEffect(
    () => {
      const strip = tabsRef.current;
      if (!strip) return undefined;

      const onWheel = (event) => {
        if ((event.deltaY === 0) || (strip.scrollWidth <= strip.clientWidth)) return;
        event.preventDefault();
        strip.scrollLeft += event.deltaY;
      };
      strip.addEventListener('wheel', onWheel, { passive: false });

      return () => strip.removeEventListener('wheel', onWheel);
    },
    [],
  );

  // A side where tabs continue out of view fades out, whatever the header sits on:
  // the mask makes the tabs themselves transparent there. Measured on scroll, on
  // resize and when tabs are added or removed
  const [hiddenSides, setHiddenSides] = useState({ left: false, right: false });
  useEffect(
    () => {
      const strip = tabsRef.current;
      if (!strip) return undefined;

      const measure = () => {
        const left = strip.scrollLeft > 1;
        const right = (strip.scrollLeft + strip.clientWidth) < (strip.scrollWidth - 1);
        setHiddenSides((prev) => (((prev.left === left) && (prev.right === right))
          ? prev
          : { left, right }));
      };
      measure();
      strip.addEventListener('scroll', measure);
      const observer = new ResizeObserver(measure);
      observer.observe(strip);
      Array.from(strip.children).forEach((tab) => observer.observe(tab));

      return () => {
        strip.removeEventListener('scroll', measure);
        observer.disconnect();
      };
    },
    [choices.length],
  );

  let maskImage;
  if (hiddenSides.left && hiddenSides.right) {
    maskImage = `linear-gradient(to right, transparent, black ${fadePx}px, black calc(100% - ${fadePx}px), transparent)`;
  } else if (hiddenSides.left) {
    maskImage = `linear-gradient(to right, transparent, black ${fadePx}px)`;
  } else if (hiddenSides.right) {
    maskImage = `linear-gradient(to right, black calc(100% - ${fadePx}px), transparent)`;
  }
  const stripStyle = (maskImage) ? { maskImage, WebkitMaskImage: maskImage } : undefined;

  const tabs = choices.map((thisChoice) => ((dragType)
    ? (
        <CardHeaderDragTab
          key={thisChoice.code}
          choice={thisChoice}
          selected={thisChoice.code === selected}
          onClick={onClick}
          disabled={disabled}
          dragType={dragType}
          moveChoice={moveChoice}
          compact={compact}
          aboveCaseMessages={aboveCaseMessages}
        />
      )
    : (
        <button
          type="button"
          key={thisChoice.code}
          id={thisChoice.code}
          onClick={onClick}
          className={tabClassName(
            thisChoice.code === selected,
            compact,
            thisChoice.invalid,
            aboveCaseMessages,
          )}
          disabled={disabled}
        >
          {tabContent(thisChoice)}
        </button>
      )));

  return (
    <Row
      start
      gap={0}
      crossStretch
      className={classNames(
        'flex-nowrap min-w-0 relative z-10 -mb-px',
        {
          // Keeps the last tab clear of the rounded top-right corner of the block below
          'pr-4': !compact,
          'px-2': compact && (!aboveCaseMessages),
          // The messages block sits 8px in with a 12px radius: the last tab stops 20px short
          'pl-2 pr-5': aboveCaseMessages,
        },
      )}
      fullw
    >
      {(title)
        ? (
            <div
              id={titleId}
              className={classNames(
                'flex items-center shrink-0 whitespace-nowrap font-semibold',
                {
                  'text-lg pl-2 pr-4': !compact,
                  'text-sm pl-1 pr-3': compact,
                },
              )}
            >
              {title}
            </div>
          )
        : null}

      {(leading)
        ? <div className="flex items-center px-1 shrink-0">{leading}</div>
        : null}

      {/* The strip takes the width left by the title and the actions, and scrolls inside it */}
      <div
        ref={tabsRef}
        style={stripStyle}
        className={classNames(
          'relative flex-1 min-w-0 overflow-x-auto scrollbar-hidden flex flex-row flex-nowrap items-stretch',
          {
            'gap-1': !compact,
            'gap-0.5': compact,
          },
        )}
      >
        {tabs}
      </div>

      {(trailing)
        ? <div className="flex items-center px-1 shrink-0">{trailing}</div>
        : null}
    </Row>
  );
};

CardHeader.propTypes = {
  titleId: PropTypes.string,
  choices: PropTypes.arrayOf(PropTypes.shape({
    code: PropTypes.string.isRequired,
    txt: PropTypes.string.isRequired,
    imgSrc: PropTypes.string,
    // Drawn with a red outline, for a tab whose content blocks the form
    invalid: PropTypes.bool,
  })),
  selected: PropTypes.string,
  select: PropTypes.func,
  title: PropTypes.string,
  disabled: PropTypes.bool,
  // A react-dnd item type: set it to make every tab a drag source carrying { code }
  dragType: PropTypes.string,
  // With dragType, called as (draggedCode, targetCode) when a tab lands on another one
  moveChoice: PropTypes.func,
  // Rendered between the title and the first tab, for an action on the tab row itself
  leading: PropTypes.node,
  // Rendered after the last tab, for an action on the tab row itself
  trailing: PropTypes.node,
  // Tabs a size down, for a row inside a card rather than on top of one
  compact: PropTypes.bool,
  // Tabs on a case header opening into its messages block: border colour and corner clearance follow
  aboveCaseMessages: PropTypes.bool,
};

export default CardHeader;
